// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@conklin/App/Components/Notify";
import { hasProductsWithInvalidPrice } from "@conklin/App/Store/Selectors/CartSelectors";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { parseBoolean } from "@conklin/XDL/Utilities";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    const isAuthenticated = xIsAuthenticated(state);
    return {
        cart,
        // XNG-Changes
        productsCannotBePurchased: cart?.cartLines
            ?.filter(cartLine => !cartLine.quoteRequired && !parseBoolean(cartLine.properties.isQuote))
            .some(o => o.isRestricted || !o.isActive || o.cantBuy),
        cartNotPriced: cart?.cartNotPriced,
        hasInsufficientInventory: cart?.hasInsufficientInventory && cart?.fulfillmentMethod === "Ship",
        // XNG-Changes
        hasInvalidPrice: hasProductsWithInvalidPrice(cart, isAuthenticated),
        failedToGetRealTimeInventory: cart?.failedToGetRealTimeInventory,
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({
    cart,
    // XNG-Changes
    productsCannotBePurchased,
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
}: Props) => {
    // XNG-Changes: remove !cart.isAwaitingApproval
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    // XNG-Change: use Notify
    return (
        <>
            {cart.isAwaitingApproval && (
                <Notify type="info" fullWidth={true} showClose={false} data-test-selector="cart_approvingOrderMessage">
                    {translate("Approving Order for {0}", cart.initiatedByUserName)}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify
                    type="warning"
                    showClose={false}
                    fullWidth={true}
                    data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                >
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {cartNotPriced && (
                <Notify
                    type="warning"
                    showClose={false}
                    fullWidth={true}
                    data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                >
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {hasInsufficientInventory && (
                <Notify
                    type="warning"
                    showClose={false}
                    fullWidth={true}
                    data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                >
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify type="warning" showClose={false} fullWidth={true}>
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify type="warning" showClose={false} fullWidth={true}>
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
